import { documents as enDocuments } from "@/lib/i18n/translations/en/documents";
import { documents as etDocuments } from "@/lib/i18n/translations/et/documents";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type {
  TemplateFormDef,
  TemplateFormFieldDef,
  WeatherOptionId,
} from "../constants/templateForms";
import { WEATHER_OPTIONS } from "../constants/templateForms";
import {
  attachmentsValue,
  peopleValue,
  textValue,
  weatherValue,
  type TemplateFormValues,
} from "./templateForm";

/**
 * The language the document itself is printed in, chosen on the form and
 * independent of the interface. The statutory forms are filed in Estonian
 * whatever language the person filling them in reads.
 */
export type PdfLocale = "et" | "en";

export function pdfCopy(locale: PdfLocale): TemplateFormTranslations {
  return locale === "en" ? enDocuments.templateForm : etDocuments.templateForm;
}

/** The document's own title, in the language it is printed in. */
export function pdfTitle(
  templateId: keyof typeof enDocuments.newDocument.templates,
  locale: PdfLocale
) {
  const bundle = locale === "en" ? enDocuments : etDocuments;
  return bundle.newDocument.templates[templateId].name;
}

/** A plain numbered row: label on the left, value on the right. */
export interface TemplateFormPdfValueRow {
  kind: "value";
  /** Absent on the additional-data block, which is not part of the statutory
   *  numbering and so is deliberately unnumbered. */
  number?: number;
  label: string;
  value: string;
}

/** The diary's weather row — a grid, not a value. */
export interface TemplateFormPdfWeatherRow {
  kind: "weather";
  number: number;
  label: string;
  timeLabel: string;
  time: string;
  temperatureLabel: string;
  temperature: string;
  options: Array<{ label: string; ticked: boolean }>;
}

/** One of the two signature blocks, each its own bordered sub-table. */
export interface TemplateFormPdfSignatureRow {
  kind: "signatures";
  number: number;
  label: string;
  columns: { nameAndCompany: string; certificate: string };
  signatories: Array<{ nameAndCompany: string; certificate: string; digitallySigned: boolean }>;
  /** `/allkirjastatud digitaalselt/`, printed under a digital signatory. */
  digitallySignedMarker: string;
  /** Whether this document is being signed digitally at all. An empty block
   *  still prints its marker line on a form that will be signed — but must not
   *  on one that will be signed by hand. */
  marksDigitalSignature: boolean;
}

export type TemplateFormPdfRow =
  | TemplateFormPdfValueRow
  | TemplateFormPdfWeatherRow
  | TemplateFormPdfSignatureRow;

/** The document as the PDF prints it — no React, no pdf-lib, just content. */
export interface TemplateFormPdfModel {
  contractorLabel: string;
  organisation: string;
  title: string;
  numberLabel: string;
  documentNumber: string;
  dateLabel: string;
  date: string;
  /** The statutory table, in its official row order. */
  rows: TemplateFormPdfRow[];
  additionalHeading: string;
  /** Franklin's own fields, kept out of the statutory numbering. */
  additionalRows: TemplateFormPdfValueRow[];
}

export interface TemplateFormPdfInput {
  form: TemplateFormDef;
  templateId: keyof typeof enDocuments.newDocument.templates;
  locale: PdfLocale;
  values: TemplateFormValues;
  documentNumber: string;
  /** Falls back to the organisation Franklin knows when the form has no such field. */
  organisation: string;
  /** The footer's toggle — unticked, the attachments row is left off the PDF. */
  attachmentsInPdf: boolean;
  /** The footer's toggle — unticked, nobody prints as digitally signed. */
  signDigitally: boolean;
}

/** `2026-07-27` → `27.07.2026`; anything else is printed as typed. */
export function formatPdfDate(value: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!parts) return value.trim();
  const [, year, month, day] = parts;
  return `${day}.${month}.${year}`;
}

/** Row 3 of both statutory forms: "<building>, <address> / <contract no>". */
function siteAndContract(values: TemplateFormValues): string {
  const place = [textValue(values, "buildingName"), textValue(values, "buildingAddress")]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
  // The separator prints even with no contract number — the blank form carries
  // it, and its absence would read as a different field.
  return `${place} / ${textValue(values, "contractNumber").trim()}`.trim();
}

function plainValue(
  field: TemplateFormFieldDef,
  values: TemplateFormValues,
  copy: TemplateFormTranslations,
  attachmentsInPdf: boolean
): string {
  switch (field.type) {
    case "date":
      return formatPdfDate(textValue(values, field.id));
    case "weather":
      return weatherValue(values, field.id)
        .map((option) => copy.weather[option])
        .join(", ");
    case "attachments":
      // Unticked, the attachments are not annexes to this document — they stay
      // attached to the record and filed as project documents, but the printed
      // act lists none. The row itself always prints: dropping a numbered row
      // from a statutory form leaves a hole where its reader expects one.
      if (!attachmentsInPdf) return "";
      return attachmentsValue(values, field.id)
        .map((attachment) => attachment.name)
        .join(", ");
    default:
      return textValue(values, field.id).trim();
  }
}

function signatureRow(
  field: TemplateFormFieldDef,
  input: TemplateFormPdfInput,
  copy: TemplateFormTranslations
): TemplateFormPdfSignatureRow {
  const people = peopleValue(input.values, field.id);
  return {
    kind: "signatures",
    number: field.statutoryRow as number,
    label: copy.pdf.rows[field.id as keyof TemplateFormTranslations["pdf"]["rows"]],
    columns: copy.pdf.signatureColumns,
    signatories: people
      .map((person) => ({
        nameAndCompany: [person.name.trim(), person.company.trim()].filter(Boolean).join(", "),
        certificate: person.certificate.trim(),
        digitallySigned: input.signDigitally && person.signer,
      }))
      // A row nobody filled in is not a party to the document. The blank
      // sub-table still prints — the renderer draws an empty row.
      .filter((entry) => entry.nameAndCompany || entry.certificate),
    digitallySignedMarker: copy.pdf.digitallySigned,
    marksDigitalSignature: input.signDigitally,
  };
}

function weatherRow(
  input: TemplateFormPdfInput,
  copy: TemplateFormTranslations
): TemplateFormPdfWeatherRow {
  const selected = weatherValue(input.values, "weather") as WeatherOptionId[];
  const temperature = textValue(input.values, "temperature").trim();
  return {
    kind: "weather",
    number: input.form.print?.weatherRow as number,
    label: copy.pdf.rows.weather,
    timeLabel: copy.pdf.weatherGrid.time,
    time: textValue(input.values, "inspectionTime").trim(),
    temperatureLabel: copy.pdf.weatherGrid.temperature,
    // The unit prints whether or not a reading was taken, as on the blank form.
    temperature: temperature ? `${temperature} ºC` : "ºC",
    options: WEATHER_OPTIONS.map((option) => ({
      label: copy.weather[option],
      ticked: selected.includes(option),
    })),
  };
}

/** Fields the header prints, plus the three that make up the composite row 3. */
const HEADER_FIELDS = new Set(["date", "organisation"]);
const SITE_FIELDS = new Set(["buildingName", "buildingAddress", "contractNumber"]);

/**
 * The printable document, built from the same `TEMPLATE_FORMS` schema the form
 * renders. Rows carrying a `statutoryRow` print in the official table at
 * exactly that number; everything else is Franklin's own and prints below it,
 * so the statutory document keeps the numbering its reader expects.
 */
export function buildTemplateFormPdfModel(input: TemplateFormPdfInput): TemplateFormPdfModel {
  const { form, values } = input;
  const copy = pdfCopy(input.locale);

  const statutory: TemplateFormPdfRow[] = [];
  const additionalRows: TemplateFormPdfValueRow[] = [];

  if (form.print) {
    statutory.push({
      kind: "value",
      number: form.print.siteAndContractRow,
      label: copy.pdf.rows.siteAndContract,
      value: siteAndContract(values),
    });
    if (form.print.weatherRow !== undefined) statutory.push(weatherRow(input, copy));
  }

  for (const section of form.sections) {
    for (const field of section.fields) {
      if (HEADER_FIELDS.has(field.id) || SITE_FIELDS.has(field.id)) continue;
      // The weather grid already carries these three.
      if (
        form.print?.weatherRow !== undefined &&
        ["weather", "temperature", "inspectionTime"].includes(field.id)
      ) {
        continue;
      }

      if (field.statutoryRow === undefined) {
        if (field.type === "people") continue;
        additionalRows.push({
          kind: "value",
          label: copy.fields[field.id],
          value: plainValue(field, values, copy, input.attachmentsInPdf),
        });
        continue;
      }
      if (field.type === "people") {
        statutory.push(signatureRow(field, input, copy));
        continue;
      }
      statutory.push({
        kind: "value",
        number: field.statutoryRow,
        label: copy.pdf.rows[field.id as keyof TemplateFormTranslations["pdf"]["rows"]],
        value: plainValue(field, values, copy, input.attachmentsInPdf),
      });
    }
  }

  statutory.sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  return {
    contractorLabel: copy.pdf.contractor,
    organisation: textValue(values, "organisation").trim() || input.organisation,
    title: pdfTitle(input.templateId, input.locale).toLocaleUpperCase("et-EE"),
    numberLabel: copy.pdf.numberLabels[form.numberLabel],
    documentNumber: input.documentNumber,
    dateLabel: copy.pdf.date,
    date: formatPdfDate(textValue(values, "date")),
    rows: statutory,
    additionalHeading: copy.pdf.additionalData,
    // Only what was actually filled in — a blank extra row would read as a
    // field of the statutory form that someone forgot to complete.
    additionalRows: additionalRows.filter((row) => row.value),
  };
}
